import { Inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { PermissionLevel, PropertySet } from '@miq/domain/property-sets';
import { STATE_PROVIDER, StateProvider } from '../services/state-provider/state-provider';
import {
  PropertySetDetailsMode,
  PropertySetMode,
  PropertySetsState,
  PropertySetView,
} from '../property-sets-dialog/property-sets-dialog';
import {
  PropertySetsDialogActions,
  PropertySetsDialogActionsProps,
} from '../property-sets-dialog-actions/property-sets-dialog-actions';
import { PropertySetService } from './property-set.service';
import { PropertySetsHttpService } from '../services/http/property-sets-http.service';
import { CbreAlertService } from '@cbre/alerts';
import { MatDialogRef } from '@angular/material/dialog';
import { PropertySetsDialogComponent } from '../property-sets-dialog/property-sets-dialog.component';
import { DialogService } from '@cbre/components';
import { PropertySetInfoDialogComponent } from './property-set-info-dialog/property-set-info-dialog.component';
import { L10nTranslationService } from 'angular-l10n';

@Injectable()
export class PropertySetDetailsDialogActionsService implements PropertySetsDialogActions<PropertySetsState> {
  constructor(
    @Inject(STATE_PROVIDER) private propertySetsStateProvider: StateProvider<PropertySetsState>,
    private propertySetsService: PropertySetService,
    private propertySetsHttpService: PropertySetsHttpService,
    private cbreAlertService: CbreAlertService,
    public dialogRef: MatDialogRef<PropertySetsDialogComponent>,
    public dialogService: DialogService,
    private translationService: L10nTranslationService
  ) {}

  tickDialogProps(state?: PropertySetsState): PropertySetsDialogActionsProps {
    let title = this.getTitle(state);
    let subtitle = this.getSubtitle(state);
    let showActions = state.dialogMode === PropertySetMode.Manage;
    let saveButtonText = showActions ? 'Add' : 'Save';

    if (state.mode === PropertySetDetailsMode.Edit) {
      const isNewPS = state.id === -1;
      showActions = true;
      title = isNewPS ? 'Create New Property Set' : 'Edit Property Set Details';
      subtitle = isNewPS && showActions ? subtitle : '';
      saveButtonText = 'Save';
    }

    const isPristine = this._compareObjects(state.propertySetChange, state.propertySet);
    const canSave =
      state?.propertySet?.level !== PermissionLevel.Read &&
      (state.mode === PropertySetDetailsMode.Read || (!isPristine && !!state.valid));
    const saveTooltipText =
      state?.propertySet?.level === PermissionLevel.Read ? 'View Only access. Cannot add or remove Records.' : null;

    return {
      title,
      subtitle,
      hasBackButton: state.hasBackButton,
      showActions,
      canSave,
      saveButtonText,
      saveTooltipText,
    };
  }

  handleSave(state?: PropertySetsState) {
    return state.mode === PropertySetDetailsMode.Read
      ? this._handleAddToPropertySet(state)
      : this._handleSaveMetadata(state);
  }

  private _handleAddToPropertySet = (state?: PropertySetsState) => {
    const ids = this.propertySetsStateProvider.snapshot().ids;
    return this.propertySetsHttpService.addPropertiesToPropertySet(state.propertySet.id, ids).pipe(
      switchMap(() => {
        const options = { panelClass: 'alert-modals' };
        const data = {
          context: 'add',
          addingMessage: this.translationService.translate('propertySet.addingPropertySetNotice', {
            recordCount: ids.length,
            propetSetName: state?.propertySet?.name,
            propertyType: state?.externalUsageType, // it is passed which is used to show in the message
          }),
        };
        this.dialogRef.close(true);
        return this.dialogService.openDialog(PropertySetInfoDialogComponent, data, options).pipe(map(() => true));
      }),
      catchError(() => {
        this.cbreAlertService.showErrorSnackBar(
          `Something went wrong while trying to add ${ids.length} Records to the ${state.propertySet.name} Property Set. Please try again.`,
          { duration: 6000 }
        );
        this.dialogRef.close(true);
        return of(true);
      })
    );
  };

  private _handleSaveMetadata = (state?: PropertySetsState) => {
    // default to update

    if (state?.propertySetChange && !state.propertySetChange?.is_statistical) {
      state.propertySetChange.publishing_sector = null;
      state.propertySetChange.publishing_group = null;
    }
    let save$: Observable<PropertySet> = this.propertySetsService.update(state.propertySetChange).pipe(
      map(() => {
        // property set object to patch should hold the old details and replace the new details,
        // else, many attributes are gone.
        return { ...state.propertySet, ...state.propertySetChange };
      })
    );

    if (state.id === -1) {
      save$ = this.propertySetsService.create(state.propertySetChange);
    }

    return save$.pipe(
      tap(this._handleSave),
      map(() => true),
      catchError(() => of(true))
    );
  };

  private _handleSave = (propertySet: PropertySet): void => {
    const { id } = propertySet;
    const defaultOpts = { mode: PropertySetDetailsMode.Read, dirty: false, valid: true };
    //making propertySetChange as null to avoid re-rendering old data
    this.propertySetsStateProvider.patchState({ ...defaultOpts, id, propertySet, propertySetChange: null });
  };

  /**
   * If details state is in edit mode then it cancel edit and sets to read, if state is not dirty and not in edit mode
   * then set the view to list.
   */
  handleCancel(): void {
    const state = this.propertySetsStateProvider.snapshot();
    if (state.mode === 'edit' && state.id !== -1) {
      this.propertySetsStateProvider.patchState({ mode: PropertySetDetailsMode.Read });
    } else {
      this.propertySetsStateProvider.patchState({
        view: PropertySetView.List,
        id: null,
        hasBackButton: false,
        propertySet: null,
        propertySetChange: null,
      });
    }
  }

  handleBack(): void {
    this.propertySetsStateProvider.patchState({
      view: PropertySetView.List,
      id: null,
      hasBackButton: false,
      propertySet: null,
      propertySetChange: null,
    });
  }

  private getTitle(state: PropertySetsState): string {
    return this._isPropertySetSelection(state) ? 'Add To Property Set' : 'My Property Sets';
  }

  private getSubtitle(state: PropertySetsState): string {
    const { ids = [], filters = {}, entity = '', source } = state;
    const [propertyType] = filters.property_types ?? [];
    const isPropertySetSelection = this._isPropertySetSelection(state);
    const entityArr = entity.split('');
    const entityTitleCase = `${entityArr[0]?.toUpperCase()}${entityArr.slice(1).join('')}`;

    const subtitle = `Click “Add” to add ${ids.length} ${propertyType ?? ''} ${entityTitleCase} Record${
      ids.length === 1 ? '' : 's'
    }${source != null ? ` from the ${source}` : ''} to this Property Set, or “Cancel” to go back.`;
    return isPropertySetSelection ? subtitle : '';
  }

  private _isPropertySetSelection(state: PropertySetsState): boolean {
    const { ids, dialogMode } = state;
    return dialogMode === PropertySetMode.Manage && ids?.length > 0;
  }

  private _compareObjects(propertySetChange: any, propertySet: any): boolean {
    if (!propertySetChange) {
      //initially obj1 is null  means no changes
      return true;
    }
    return Object.keys(propertySetChange).every(key => propertySetChange[key] === propertySet[key]);
  }
}
